import { useQueryClient, useMutation } from "@tanstack/react-query";
import FetchClients from "../client";
import { PROJECT_KEY, useInvalidateProjectsPortfolioSummary } from "./project";
import request from "@src/global_functions/request";
import { attachmentCipher } from "@src/global_functions/attachments/attachmentUtils";
import {
  bugsnagGeneralErrorHandler,
  bugsnagPostgrestErrorHandler,
} from "@src/global_functions/postgrestApi/common";
import { VITE_UPLOAD_BASE_URL } from "@src/env";
import { CheckFeatureAccessHeader } from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

const attachments = {
  getRewardReceiptPreviews,
  uploadRewardReceipt,
  downloadRewardReceipt,
  getRewardReceiptInformation,
  useDeleteReceipts,
};

// base key for every attachment type -- the type is the next key segment
export const ATTACHMENT_PREVIEW_BASE_KEY = "attachment-preview";

const rewardsBaseUrl = `${VITE_UPLOAD_BASE_URL}/reward-receipt`;
async function getRewardReceiptPreviews(reward_id: number): Promise<{
  [key: string]: string;
}> {
  return request
    .get(`${rewardsBaseUrl}/preview`)
    .query({ reward_id })
    .then((res) => res.body)
    .catch((error) => bugsnagGeneralErrorHandler(error));
}

// classified under the "Rebates" feature (see SERVICE_FEATURE_ACCESS_MAP in
// attachmentsQueries.tsx); header is optional so this stays call-compatible with
// the shared serviceMap union, and the consumer always supplies it.
async function uploadRewardReceipt(
  project_id: number,
  file: File,
  checkFeatureAccessHeader: CheckFeatureAccessHeader = {}
): Promise<string | void> {
  return request
    .post(`${rewardsBaseUrl}/upload`)
    .set("Content-Type", "text/plain")
    .set(checkFeatureAccessHeader)
    .query({ project_id, filename: file.name })
    .send(file)
    .then((res) => res.text)
    .catch((error) => bugsnagGeneralErrorHandler(error));
}

async function downloadRewardReceipt(
  receipt_id: number
): Promise<string | void> {
  return request
    .get(`${rewardsBaseUrl}/download`)
    .query({ receipt_id })
    .then((res) => res.text)
    .catch((error) => bugsnagGeneralErrorHandler(error));
}

// endpoint requires {feature: "Projects", access: "read"}
// NOTE: The remaining reward-receipt endpoints in this file (previews, download,
// useDeleteReceipts) hit the shared D3 reward-receipt service and are
// classified under the "Rebates" feature in attachmentsQueries.ts
// (SERVICE_FEATURE_ACCESS_MAP) — their access-check wiring is handled with Rebates.
async function getRewardReceiptInformation(
  project_id: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader = {}
) {
  const { data, error } = await FetchClients.projects.GET(
    "/{project_id}/reward_receipts",
    {
      params: {
        path: {
          project_id,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) {
    return attachmentCipher(data);
  }
  bugsnagPostgrestErrorHandler(error);
  return [];
}
// service is in D3, but break from shared functionality with projects attachments
function useDeleteReceipts(site_id: number, project_id?: number) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutation({
    mutationFn: async (rewardReceiptIds: number[]) => {
      const promises = rewardReceiptIds.map((reward_receipt_id) =>
        FetchClients.d3.DELETE("/reward-receipt/{reward_receipt_id}", {
          params: {
            path: { reward_receipt_id },
          },
        })
      );
      const results = await Promise.all(promises);
      results.forEach(({ response }) => {
        if (!response.ok) {
          throw new Error(
            `Failed to delete an unsubmitted receipt: ${response.statusText}`
          );
        }
      });
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY, site_id],
        }),
        queryClient.invalidateQueries({
          queryKey: [ATTACHMENT_PREVIEW_BASE_KEY, "rewards", project_id],
        }),
        invalidatePortfolioSummary(),
      ]),
    onError: (e) => {
      throw new Error(
        `Unable to clean up unsubmitted receipt for project ${project_id}, site ${site_id}
        ${(e as Error)?.message || "Unknown Error"}`
      );
    },
  });
}

export default attachments;
